import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Snackbar,
  Alert,
  Button,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Close as CloseIcon, GetApp as GetAppIcon, Share as ShareIcon } from '@mui/icons-material';

export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

const PWAInstallPrompt: React.FC = () => {
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(
    (window as any).deferredPrompt || null
  );
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showManualInstructions, setShowManualInstructions] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [hasInstalled, setHasInstalled] = useState(false);
  const [platform, setPlatform] = useState<'ios' | 'android' | 'other'>('other');

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches || 
        (window.navigator as any).standalone === true) {
      setHasInstalled(true);
      return;
    }

    // Enhanced mobile detection for iPhone and Android
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
      setIsMobile(isMobileDevice);
      
      // Enhanced platform detection
      if (/iphone|ipad|ipod/i.test(userAgent.toLowerCase())) {
        setPlatform('ios');
      } else if (/android/i.test(userAgent.toLowerCase())) {
        setPlatform('android');
      } else {
        setPlatform('other');
      }
    };

    checkMobile();

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      (window as any).deferredPrompt = e;
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      
      // Show prompt after a delay for mobile users
      if (isMobile) {
        setTimeout(() => {
          setShowInstallPrompt(true);
        }, 2000); // Show after 2 seconds
      }
    };

    const handlePromptReady = () => {
      if ((window as any).deferredPrompt) {
        setDeferredPrompt((window as any).deferredPrompt);
        if (isMobile) {
          setShowInstallPrompt(true);
        }
      }
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      (window as any).deferredPrompt = null;
      setHasInstalled(true);
      setShowInstallPrompt(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('pwa-prompt-ready', handlePromptReady);
    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener('pwa-app-installed', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('pwa-prompt-ready', handlePromptReady);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('pwa-app-installed', handleAppInstalled);
    };
  }, [isMobile]);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setHasInstalled(true);
        setShowInstallPrompt(false);
      }
      
      setDeferredPrompt(null);
    } else {
      // Show manual instructions for iOS or when prompt is not available
      setShowManualInstructions(true);
    }
  };

  const handleClose = () => {
    setShowInstallPrompt(false);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Atlas.Rest',
          text: 'Check out this amazing restaurant discovery app!',
          url: window.location.href,
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    }
  };

  const getInstallInstructions = () => {
    switch (platform) {
      case 'ios':
        return (
          <Box>
            <Typography variant="body2" gutterBottom>
              To add Atlas.Rest to your home screen:
            </Typography>
            <Typography variant="body2" component="ol" sx={{ pl: 2 }}>
              <li>Tap the Share button <ShareIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} /></li>
              <li>Scroll down and tap "Add to Home Screen"</li>
              <li>Tap "Add" to confirm</li>
            </Typography>
          </Box>
        );
      case 'android':
        return (
          <Box>
            <Typography variant="body2" gutterBottom>
              To install Atlas.Rest:
            </Typography>
            <Typography variant="body2" component="ol" sx={{ pl: 2 }}>
              <li>Tap the menu button (⋮) in your browser</li>
              <li>Select "Add to Home screen" or "Install app"</li>
              <li>Follow the prompts to complete installation</li>
            </Typography>
          </Box>
        );
      default:
        return (
          <Typography variant="body2">
            Use your browser's menu to add this app to your home screen for a better experience.
          </Typography>
        );
    }
  };

  // Don't show if already installed, not mobile, or currently on the dedicated install page
  if (hasInstalled || !isMobile || !showInstallPrompt || location.pathname === '/install') {
    return null;
  }

  return (
    <>
      <Snackbar
        open={showInstallPrompt}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbar-root': {
            bottom: 100, // Position 100px above bottom navigation
          },
          '& .MuiAlert-root': {
            width: '100%',
            maxWidth: 400,
            backgroundColor: '#1976d2',
            color: 'white',
            '& .MuiAlert-message': {
              color: 'white',
            },
          },
        }}
      >
        <Alert
          severity="info"
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                color="inherit"
                size="small"
                startIcon={<GetAppIcon />}
                onClick={handleInstallClick}
                sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  }
                }}
              >
                Install
              </Button>
              <IconButton
                size="small"
                color="inherit"
                onClick={handleClose}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          }
        >
          <Typography variant="body2" sx={{ color: 'white' }}>
            Add Atlas.Rest to your home screen for a better experience!
          </Typography>
        </Alert>
      </Snackbar>

      <Dialog
        open={showManualInstructions}
        onClose={() => setShowManualInstructions(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Install Atlas.Rest
        </DialogTitle>
        <DialogContent>
          {getInstallInstructions()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowManualInstructions(false)}>
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PWAInstallPrompt; 